import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import {
  CalendarOff, CalendarPlus, Clock, Loader2, Pencil, Plus, RotateCcw, Settings2, Trash2,
} from 'lucide-react';
import {
  listAgendas,
  listConceptos,
  createConcepto,
  updateConcepto,
  deleteConcepto,
  listBloqueos,
  deleteBloqueo,
  esDeAgendasDisponibles,
  todayStr,
  addDaysStr,
  strToDate,
  MODOS_RESERVA,
  MODO_RESERVA_POR_DEFECTO,
  MODOS_RESERVA_AUTOMATICOS,
  normalizarTextoCobro,
} from '../lib/citasApi';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { isAdmin } from '../lib/roles';
import { PageHeader } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import AgendaConfigDialog from '../components/citas/AgendaConfigDialog';
import AgendaNuevaDialog from '../components/citas/AgendaNuevaDialog';
import BloqueoDialog from '../components/citas/BloqueoDialog';

/**
 * Gestión de agendas (solo admin): horarios semanales de cada agenda,
 * cierres/aperturas extraordinarios y catálogo de conceptos de cita.
 *
 * Reutiliza AgendaConfigDialog y BloqueoDialog de la vista de citas.
 *
 * «Nueva agenda» (30/09/2026, solo administradores): da de alta una agenda,
 * copiando si se quiere la configuración de otra (AgendaNuevaDialog). No se
 * borran agendas desde aquí.
 *
 * Horario sin revisar (15/10/2026): la agenda que abre de lunes a viernes sin
 * que nadie haya revisado su horario (`horario_por_revisar`, del servidor)
 * lleva el distintivo «Horario sin revisar (abre de lunes a viernes)», y
 * arriba se dice cuántas son. En «Configurar» se le cierran los días que no
 * pasa consulta o se marca como revisado. `?configurar=<id>` (el enlace del
 * aviso de la vista «Mes») abre «Configurar» de esa agenda.
 */

const DAY_SHORT = ['L', 'M', 'X', 'J', 'V', 'S', 'D']; // 0 = lunes (criterio backend)

/** Por qué avisa una agenda que abre de lunes a viernes (el `title` del distintivo). */
const POR_QUE_HORARIO_SIN_REVISAR = 'Esta agenda abre de lunes a viernes y nadie ha revisado su horario: '
  + 'la vista «Mes» de la Agenda la da como abierta todos los días laborables. En «Configurar», '
  + 'ciérrale los días que no pasa consulta o, si es correcto así, márcalo allí como revisado.';

/** Agrupa días consecutivos con el mismo horario: [{label:"L-V", text:"09:00–14:00 / 15:00–21:00", closed}] */
function scheduleGroups(workingHours) {
  const sig = (i) =>
    ((workingHours || {})[String(i)] || []).map((r) => `${r.start}–${r.end}`).join(' / ');
  const groups = [];
  for (let i = 0; i < 7; i++) {
    const s = sig(i);
    const last = groups[groups.length - 1];
    if (last && last.sig === s) last.to = i;
    else groups.push({ from: i, to: i, sig: s });
  }
  return groups.map((g) => ({
    label: g.from === g.to ? DAY_SHORT[g.from] : `${DAY_SHORT[g.from]}-${DAY_SHORT[g.to]}`,
    text: g.sig || 'Cerrado',
    closed: !g.sig,
  }));
}

/** "YYYY-MM-DD" → "dd/mm/yyyy" (local, sin sorpresas UTC). */
function fmtDate(dateStr) {
  if (!dateStr) return '—';
  return strToDate(dateStr).toLocaleDateString('es-ES');
}

/** Orden del catálogo: sort_order y, a igualdad, nombre. */
const ordenarConceptos = (lista) => lista
  .slice()
  .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || String(a.name).localeCompare(String(b.name), 'es'));

/** Nombre → slug para el code del concepto ("Consulta previa" → "consulta-previa"). */
function slugify(s) {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function AgendaPill({ agenda, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`gestion-agendas-pill-${agenda.code || agenda.id}`}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm whitespace-nowrap transition-colors ${
        selected
          ? 'bg-[#2E6E5E] text-white border-[#2E6E5E] font-medium'
          : 'bg-white text-[#13231E] border-[#dbe3ee] hover:border-[#2E6E5E] hover:bg-[rgba(46,110,94,0.06)]'
      }`}
    >
      <span
        className="w-2.5 h-2.5 rounded-full shrink-0"
        style={{ backgroundColor: agenda.color || '#2E6E5E' }}
        aria-hidden="true"
      />
      {agenda.name}
    </button>
  );
}

function SectionTitle({ title, note, action }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-2 mb-3">
      <div>
        <h2 className="font-display text-lg font-semibold text-[#13231E]">{title}</h2>
        {note && <p className="text-xs text-muted-foreground mt-0.5">{note}</p>}
      </div>
      {action && <div className="flex flex-wrap gap-2">{action}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Diálogo de creación/edición de concepto de cita
// ---------------------------------------------------------------------------

/**
 * Las MARCAS de un tipo de cita (services/cita_conceptos.py): lo que las
 * reglas de la casa preguntan en vez del código. Solo admin, con rastro.
 */
export const MARCAS_CONCEPTO = [
  {
    clave: 'es_intervencion',
    etiqueta: 'Es una intervención',
    ayuda: 'Abre el episodio de la ficha al marcarla «En recuperación» o «Se fue» y el cuestionario no pide fotos. Un tipo de categoría «Cirugía» ya abre el episodio.',
    corto: 'Intervención',
  },
  {
    clave: 'es_retoque',
    etiqueta: 'Es un retoque',
    ayuda: 'En quirófano, Confirmación y Recordatorio mandan las plantillas de retoque y la guía del paciente sale en su variante (sin pruebas, sin pauta, sin pago).',
    corto: 'Retoque',
  },
  {
    clave: 'es_chalazion',
    etiqueta: 'Es el procedimiento menor (chalazión)',
    ayuda: 'Se cita en consulta o en quirófano: lleva su consentimiento de cirugía menor y su hoja de postoperatorio esté donde esté citado.',
    corto: 'Chalazión',
  },
  {
    clave: 'lee_la_nota',
    etiqueta: 'La nota dice qué se opera',
    ayuda: 'Para un tipo genérico o un tratamiento: la hoja de postoperatorio se propone por lo que diga la nota. Sin marcar, una consulta o una revisión nunca es una intervención aunque la nota nombre una.',
    corto: 'Lee la nota',
  },
  {
    clave: 'es_consulta_previa',
    etiqueta: 'Es una consulta previa',
    ayuda: 'La primera visita del paciente que viene a que le valoren (presencial u online). La cuenta el ranking de consultas previas agendadas por usuario de Indicadores.',
    corto: 'Consulta previa',
  },
  {
    // Reservas en las agendas (12/10/2026): qué tipos las admiten es un dato
    // de la clínica, no una lista del programa. Cómo se pagan (15/10/2026),
    // también: «Cómo se paga la reserva» y «Cobros que la pagan».
    clave: 'admite_reserva',
    etiqueta: 'Admite reserva',
    ayuda: 'Al dar cita se ofrece también «Reserva …» de este tipo: la cita sale en amarillo con la etiqueta RESERVA hasta que se paga como digas abajo, o hasta que se elige el tipo normal.',
    corto: 'Reserva',
  },
];
const SIN_HOJA = '__ninguna__';

/** Los textos de «Cobros que la pagan»: uno por línea (o separados por comas). */
export function textosDeCobros(texto) {
  const vistos = new Set();
  return String(texto || '').split(/[\n,]/)
    .map((t) => t.trim().split(/\s+/).filter(Boolean).join(' '))
    .filter((t) => {
      const clave = normalizarTextoCobro(t);
      if (!clave || vistos.has(clave)) return false;
      vistos.add(clave);
      return true;
    });
}

/** ¿Un modo que se paga solo pero sin ningún cobro? Entonces no se paga sola. */
export function reservaSinCobros(concepto) {
  return !!concepto?.admite_reserva
    && MODOS_RESERVA_AUTOMATICOS.includes(concepto?.reserva_modo || MODO_RESERVA_POR_DEFECTO)
    && !(concepto?.reserva_cobros || []).length;
}

const MINIMO_TEXTO_COBRO = 3;

function ConceptoDialog({ open, onOpenChange, initial, onSaved }) {
  const isEdit = !!initial?.id;
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [codeTouched, setCodeTouched] = useState(false);
  const [category, setCategory] = useState('consulta');
  const [minutes, setMinutes] = useState('15');
  const [color, setColor] = useState('#2E6E5E');
  const [sortOrder, setSortOrder] = useState('0');
  const [marcas, setMarcas] = useState({});
  // Cómo se paga la reserva y con qué cobros (15/10/2026).
  const [reservaModo, setReservaModo] = useState(MODO_RESERVA_POR_DEFECTO);
  const [reservaCobros, setReservaCobros] = useState('');
  const [pauta, setPauta] = useState('');
  const [plantillas, setPlantillas] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let vigente = true;
    api.get('/api/postoperatorios/templates')
      .then((r) => { if (vigente) setPlantillas(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (vigente) setPlantillas([]); });
    return () => { vigente = false; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setMarcas(Object.fromEntries(MARCAS_CONCEPTO.map((m) => [m.clave, !!initial?.[m.clave]])));
    setReservaModo(initial?.reserva_modo || MODO_RESERVA_POR_DEFECTO);
    setReservaCobros((initial?.reserva_cobros || []).join('\n'));
    setPauta(initial?.pauta_postop || '');
    setName(initial?.name || '');
    setCode(initial?.code || '');
    setCodeTouched(!!initial?.id);
    setCategory(initial?.category || 'consulta');
    setMinutes(String(initial?.default_minutes ?? 15));
    setColor(initial?.color || '#2E6E5E');
    setSortOrder(String(initial?.sort_order ?? 0));
    setSaving(false);
  }, [open, initial]);

  const onChangeName = (v) => {
    setName(v);
    if (!isEdit && !codeTouched) setCode(slugify(v));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error('El nombre del concepto es obligatorio');
    if (!isEdit && !slugify(code)) return toast.error('El código (slug) es obligatorio');
    const mins = Number(minutes);
    if (!Number.isFinite(mins) || mins <= 0) return toast.error('La duración debe ser un número de minutos mayor que 0');
    const order = Number(sortOrder);
    if (!Number.isFinite(order)) return toast.error('El orden debe ser un número');
    const textos = textosDeCobros(reservaCobros);
    const corto = textos.find((t) => normalizarTextoCobro(t).length < MINIMO_TEXTO_COBRO);
    if (corto) {
      return toast.error(`«${corto}» es demasiado corto para «Cobros que la pagan»: casaría con casi cualquier cobro.`);
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        category,
        default_minutes: mins,
        color,
        sort_order: order,
        ...Object.fromEntries(MARCAS_CONCEPTO.map((m) => [m.clave, !!marcas[m.clave]])),
        reserva_modo: reservaModo,
        reserva_cobros: textos,
        pauta_postop: pauta || '',
      };
      if (isEdit) {
        await updateConcepto(initial.id, payload);
        toast.success('Concepto actualizado');
      } else {
        await createConcepto({ ...payload, code: slugify(code) });
        toast.success('Concepto creado');
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo guardar el concepto');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="concepto-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar concepto' : 'Nuevo concepto de cita'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Nombre</Label>
            <Input
              value={name}
              onChange={(e) => onChangeName(e.target.value)}
              placeholder="Ej.: Consulta previa"
              className="mt-1.5"
              data-testid="concepto-name"
            />
          </div>

          <div>
            <Label>Código (slug)</Label>
            <Input
              value={code}
              disabled={isEdit}
              onChange={(e) => {
                setCodeTouched(true);
                setCode(slugify(e.target.value));
              }}
              placeholder="consulta-previa"
              className="mt-1.5 font-mono text-sm"
              data-testid="concepto-code"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {isEdit ? 'El código no se puede cambiar una vez creado.' : 'Identificador único; se genera solo a partir del nombre.'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Categoría</Label>
              <Select
                value={category}
                onValueChange={(v) => {
                  setCategory(v);
                  // Un tipo NUEVO de cirugía nace como intervención (se puede desmarcar).
                  if (!isEdit && v === 'cirugia') setMarcas((m) => ({ ...m, es_intervencion: true }));
                }}
              >
                <SelectTrigger className="mt-1.5" data-testid="concepto-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="consulta">Consulta</SelectItem>
                  <SelectItem value="cirugia">Cirugía</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Duración (min)</Label>
              <Input
                type="number"
                min={5}
                step={5}
                value={minutes}
                onChange={(e) => setMinutes(e.target.value)}
                className="mt-1.5 tabular-nums"
                data-testid="concepto-minutes"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Color</Label>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="mt-1.5 block h-10 w-14 rounded-md border border-input bg-white p-1 cursor-pointer"
                aria-label="Color del concepto"
                data-testid="concepto-color"
              />
            </div>
            <div>
              <Label>Orden</Label>
              <Input
                type="number"
                min={0}
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="mt-1.5 tabular-nums"
                data-testid="concepto-sort-order"
              />
            </div>
          </div>

          <fieldset className="space-y-2 rounded-md border border-[#dbe3ee] p-3" data-testid="concepto-marcas">
            <legend className="px-1 text-xs font-medium uppercase tracking-wide text-[#55677a]">Marcas del tipo</legend>
            {MARCAS_CONCEPTO.map((m) => (
              <label key={m.clave} className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 accent-[#2E6E5E]"
                  checked={!!marcas[m.clave]}
                  onChange={(e) => setMarcas((prev) => ({ ...prev, [m.clave]: e.target.checked }))}
                  data-testid={`concepto-marca-${m.clave}`}
                />
                <span>
                  <span className="font-medium text-[#13231E]">{m.etiqueta}</span>
                  <span className="block text-xs text-muted-foreground">{m.ayuda}</span>
                </span>
              </label>
            ))}
            {/* Cómo se paga la reserva (15/10/2026): el modo y los cobros que
                la pagan son de la clínica. En «a mano» no hay cobros. */}
            {marcas.admite_reserva && (
              <div className="ml-6 space-y-2 rounded-md border border-dashed border-[#dbe3ee] p-2.5" data-testid="concepto-reserva">
                <div>
                  <Label>Cómo se paga la reserva</Label>
                  <Select value={reservaModo} onValueChange={setReservaModo}>
                    <SelectTrigger className="mt-1.5 h-auto min-h-9 text-left whitespace-normal" data-testid="concepto-reserva-modo">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MODOS_RESERVA.map((m) => (
                        <SelectItem key={m.valor} value={m.valor}>{m.etiqueta}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {reservaModo !== 'manual' ? (
                  <div>
                    <Label htmlFor="concepto-reserva-cobros">Cobros que la pagan</Label>
                    <Textarea
                      id="concepto-reserva-cobros"
                      rows={3}
                      value={reservaCobros}
                      onChange={(e) => setReservaCobros(e.target.value)}
                      placeholder="Un texto por línea"
                      className="mt-1.5 text-sm"
                      data-testid="concepto-reserva-cobros"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Un cobro la paga si su concepto contiene alguno de estos textos, sin mirar mayúsculas,
                      tildes ni espacios de más.
                      {reservaModo === 'cirugia'
                        ? ' Es el cobro de la reserva; el resto se cobra con su presupuesto (campo «Presupuesto» de Cobros).'
                        : ''}
                    </p>
                    {textosDeCobros(reservaCobros).length === 0 && (
                      <p className="text-xs font-medium text-amber-700 mt-1" data-testid="concepto-reserva-aviso">
                        ▸ Sin ningún cobro, la reserva no se paga sola: habrá que quitarla a mano eligiendo el tipo normal.
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Ningún cobro la paga: el equipo elige el tipo normal cuando toque y la cita vuelve a su color.
                  </p>
                )}
              </div>
            )}
            <div>
              <Label>Hoja de postoperatorio</Label>
              <Select value={pauta || SIN_HOJA} onValueChange={(v) => setPauta(v === SIN_HOJA ? '' : v)}>
                <SelectTrigger className="mt-1.5" data-testid="concepto-pauta">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SIN_HOJA}>Ninguna (la de por defecto si es intervención)</SelectItem>
                  {pauta && !plantillas.some((t) => t.code === pauta) && (
                    <SelectItem value={pauta}>{pauta}</SelectItem>
                  )}
                  {plantillas.filter((t) => t.code).map((t) => (
                    <SelectItem key={t.code} value={t.code}>
                      {t.name || t.code}{t.active === false ? ' (desactivada)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                La que se propone al dar el postoperatorio de una cita de este tipo.
              </p>
            </div>
          </fieldset>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="concepto-cancel">
              Cancelar
            </Button>
            <Button type="submit" disabled={saving} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="concepto-save">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isEdit ? 'Guardar cambios' : 'Crear concepto'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

export default function GestionAgendasPage() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  // «Configurar» abierto desde el aviso de la vista «Mes» (?configurar=<id>).
  const [searchParams, setSearchParams] = useSearchParams();
  const configurarPedida = searchParams.get('configurar');
  const [agendas, setAgendas] = useState([]);
  const [conceptos, setConceptos] = useState([]);
  const [loading, setLoading] = useState(true);

  // Sección agendas
  const [configAgenda, setConfigAgenda] = useState(null);
  const [nuevaOpen, setNuevaOpen] = useState(false);

  // Sección cierres/aperturas
  const [selAgendaId, setSelAgendaId] = useState(null);
  const [bloqueos, setBloqueos] = useState([]);
  const [loadingBloqueos, setLoadingBloqueos] = useState(false);
  const [bloqueoTipo, setBloqueoTipo] = useState(null); // 'cierre' | 'apertura' | null
  const [deletingBloqueo, setDeletingBloqueo] = useState(null);
  const [deletingBloqueoBusy, setDeletingBloqueoBusy] = useState(false);

  // Sección conceptos
  const [conceptoDialogOpen, setConceptoDialogOpen] = useState(false);
  const [editingConcepto, setEditingConcepto] = useState(null);
  const [deactivatingConcepto, setDeactivatingConcepto] = useState(null);
  const [deactivatingBusy, setDeactivatingBusy] = useState(false);
  const [reactivatingId, setReactivatingId] = useState(null);

  const selAgenda = useMemo(
    () => agendas.find((a) => a.id === selAgendaId) || null,
    [agendas, selAgendaId],
  );
  const porRevisar = useMemo(() => agendas.filter((a) => a.horario_por_revisar).length, [agendas]);

  // El enlace «Configurar» del aviso de la vista «Mes»: abre la de esa
  // agenda en cuanto llega la lista, y se quita de la dirección para que
  // recargar no la vuelva a abrir.
  useEffect(() => {
    if (!configurarPedida || loading) return;
    const pedida = agendas.find((a) => a.id === configurarPedida);
    if (pedida) setConfigAgenda(pedida);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('configurar');
      return next;
    }, { replace: true });
  }, [configurarPedida, loading, agendas, setSearchParams]);

  // El catálogo se carga CON los retirados (revisión 2026-09-03): se listan
  // atenuados y con «Reactivar», para que retirar un tipo («Cirugía» y
  // «Cirugía menor» en el punto 4) no sea irreversible desde la pantalla.
  // Activos primero; el diálogo de duraciones por agenda solo ve los activos.
  const activeConceptos = useMemo(() => ordenarConceptos(conceptos.filter((c) => c.active !== false)), [conceptos]);
  const conceptosListado = useMemo(
    () => [...activeConceptos, ...ordenarConceptos(conceptos.filter((c) => c.active === false))],
    [conceptos, activeConceptos],
  );

  const reloadAgendas = useCallback(async () => {
    const ags = await listAgendas();
    const list = Array.isArray(ags) ? ags : [];
    setAgendas(list);
    if (list.length > 0) setSelAgendaId((prev) => (prev && list.some((a) => a.id === prev) ? prev : list[0].id));
    return list;
  }, []);

  const reloadConceptos = useCallback(async () => {
    const cons = await listConceptos({ todos: true });
    setConceptos(Array.isArray(cons) ? cons : []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([listAgendas(), listConceptos({ todos: true })])
      .then(([ags, cons]) => {
        if (cancelled) return;
        const list = Array.isArray(ags) ? ags : [];
        setAgendas(list);
        setConceptos(Array.isArray(cons) ? cons : []);
        if (list.length > 0) setSelAgendaId((prev) => prev || list[0].id);
      })
      .catch((e) => {
        if (!cancelled) toast.error(e?.response?.data?.detail || 'No se pudieron cargar las agendas');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Bloqueos de la agenda seleccionada: desde hoy hasta +365 días.
  const reloadBloqueos = useCallback(async () => {
    if (!selAgendaId) return;
    setLoadingBloqueos(true);
    try {
      const hoy = todayStr();
      const bs = await listBloqueos(selAgendaId, { dateFrom: hoy, dateTo: addDaysStr(hoy, 365) });
      setBloqueos(Array.isArray(bs) ? bs : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudieron cargar los cierres y aperturas');
    } finally {
      setLoadingBloqueos(false);
    }
  }, [selAgendaId]);

  useEffect(() => {
    reloadBloqueos();
  }, [reloadBloqueos]);

  const confirmDeleteBloqueo = async () => {
    if (!deletingBloqueo) return;
    setDeletingBloqueoBusy(true);
    try {
      await deleteBloqueo(deletingBloqueo.id);
      toast.success(deletingBloqueo.tipo === 'apertura' ? 'Apertura eliminada' : 'Cierre eliminado');
      setDeletingBloqueo(null);
      reloadBloqueos();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar');
    } finally {
      setDeletingBloqueoBusy(false);
    }
  };

  // Reactivar un tipo retirado: PUT del concepto con active=true (el mismo
  // endpoint de edición; el catálogo se recarga con el flag nuevo).
  const reactivateConcepto = async (c) => {
    setReactivatingId(c.id);
    try {
      await updateConcepto(c.id, { active: true });
      toast.success(`«${c.name}» vuelve a estar disponible`);
      await reloadConceptos();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo reactivar el concepto');
    } finally {
      setReactivatingId(null);
    }
  };

  const confirmDeactivateConcepto = async () => {
    if (!deactivatingConcepto) return;
    setDeactivatingBusy(true);
    try {
      await deleteConcepto(deactivatingConcepto.id);
      toast.success('Concepto desactivado');
      setDeactivatingConcepto(null);
      reloadConceptos();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo desactivar el concepto');
    } finally {
      setDeactivatingBusy(false);
    }
  };

  const openNewConcepto = () => {
    setEditingConcepto(null);
    setConceptoDialogOpen(true);
  };
  const openEditConcepto = (c) => {
    setEditingConcepto(c);
    setConceptoDialogOpen(true);
  };

  return (
    <div data-testid="gestion-agendas-page">
      <PageHeader title="Gestión de agendas" subtitle="Horarios, cierres, aperturas y conceptos de cita" />

      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-40 w-full rounded-xl" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      ) : (
        <div className="space-y-8">
          {/* ------------------------------------------------------------ */}
          {/* Agendas                                                       */}
          {/* ------------------------------------------------------------ */}
          <section data-testid="gestion-agendas-section-agendas">
            <SectionTitle
              title="Agendas"
              note="Horario semanal, tamaño de hueco y duraciones por concepto de cada agenda."
              action={admin && (
                <Button
                  onClick={() => setNuevaOpen(true)}
                  className="h-9 bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                  data-testid="gestion-agendas-nueva"
                >
                  <Plus className="w-4 h-4 mr-1.5" /> Nueva agenda
                </Button>
              )}
            />
            {porRevisar > 0 && (
              <div
                role="note"
                className="mb-3 rounded-lg border border-amber-300/80 bg-amber-50 px-3 py-2 text-sm text-[#13231E] dark:border-amber-700/60 dark:bg-amber-950/30 dark:text-amber-50"
                data-testid="gestion-agendas-resumen-horario"
              >
                <strong className="font-semibold">
                  {porRevisar} {porRevisar === 1 ? 'agenda abre' : 'agendas abren'} de lunes a viernes sin revisar
                </strong>
                <span className="text-[#13231E]/80 dark:text-amber-100/80">
                  : la vista «Mes» de la Agenda {porRevisar === 1 ? 'la da como abierta' : 'las da como abiertas'} todos
                  los días laborables. En «Configurar», ciérrales los días que no pasan consulta o, si es correcto
                  así, márcalo allí como revisado.
                </span>
              </div>
            )}
            {agendas.length === 0 ? (
              <div className="cjo-card p-8 text-center text-sm text-muted-foreground">
                No hay agendas configuradas.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {agendas.map((a) => {
                  const overridesCount = Object.keys(a.concept_overrides || {}).length;
                  return (
                    <div key={a.id} className="cjo-card overflow-hidden flex flex-col" data-testid={`gestion-agendas-card-${a.code || a.id}`}>
                      <div className="h-1.5" style={{ backgroundColor: a.color || '#2E6E5E' }} aria-hidden="true" />
                      <div className="p-4 flex-1 flex flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="font-display font-semibold text-[#13231E]">{a.name}</div>
                            {a.doctor_name && (
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {a.doctor_name}
                                {!a.doctor_user_id && (
                                  <span title="Su profesional aún no tiene usuario en la app" data-testid={`gestion-agendas-sin-usuario-${a.code || a.id}`}>
                                    {' '}· sin usuario
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          <Badge variant="outline" className="shrink-0 text-[11px] tabular-nums gap-1">
                            <Clock className="w-3 h-3" /> Hueco: {a.slot_minutes || 15} min
                          </Badge>
                        </div>

                        <div className="mt-3 space-y-1 text-sm flex-1">
                          {scheduleGroups(a.working_hours).map((g, i) => (
                            <div key={i} className="flex gap-2">
                              <span className="w-9 shrink-0 font-medium text-[#13231E] tabular-nums">{g.label}</span>
                              <span className={g.closed ? 'text-muted-foreground/70' : 'text-foreground tabular-nums'}>
                                {g.text}
                              </span>
                            </div>
                          ))}
                        </div>

                        {overridesCount > 0 && (
                          <div className="mt-2 text-xs text-muted-foreground">
                            {overridesCount} {overridesCount === 1 ? 'duración personalizada' : 'duraciones personalizadas'}
                          </div>
                        )}
                        {/* Abre de lunes a viernes sin revisar (15/10/2026). */}
                        {a.horario_por_revisar && (
                          <div className="mt-2">
                            <Badge
                              variant="outline"
                              className="text-[11px] border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/30 dark:text-amber-100"
                              title={POR_QUE_HORARIO_SIN_REVISAR}
                              data-testid={`gestion-agendas-horario-por-revisar-${a.code || a.id}`}
                            >
                              Horario sin revisar (abre de lunes a viernes)
                            </Badge>
                          </div>
                        )}
                        {/* Las que ofrecen el médico visitante del día (07/10/2026). */}
                        {a.admite_visitante && (
                          <div className="mt-1 text-xs text-[#0E7490]" data-testid={`gestion-agendas-admite-visitante-${a.code || a.id}`}>
                            Admite médico visitante
                          </div>
                        )}

                        <div className="mt-3 pt-3 border-t border-[#F0F6F2]">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setConfigAgenda(a)}
                            data-testid={`gestion-agendas-config-${a.code || a.id}`}
                          >
                            <Settings2 className="w-4 h-4 mr-1.5" /> Configurar
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* ------------------------------------------------------------ */}
          {/* Cierres y aperturas                                           */}
          {/* ------------------------------------------------------------ */}
          <section data-testid="gestion-agendas-section-bloqueos">
            <SectionTitle
              title="Cierres y aperturas"
              note="Cierra días u horas concretas (vacaciones, congresos…) o abre tramos extraordinarios fuera del horario semanal."
              action={
                <>
                  <Button
                    variant="outline"
                    className="h-9"
                    disabled={!selAgenda}
                    onClick={() => setBloqueoTipo('cierre')}
                    data-testid="gestion-agendas-new-cierre"
                  >
                    <CalendarOff className="w-4 h-4 mr-1.5" /> Cerrar días/huecos
                  </Button>
                  <Button
                    variant="outline"
                    className="h-9"
                    disabled={!selAgenda}
                    onClick={() => setBloqueoTipo('apertura')}
                    data-testid="gestion-agendas-new-apertura"
                  >
                    <CalendarPlus className="w-4 h-4 mr-1.5" /> Abrir huecos extra
                  </Button>
                </>
              }
            />

            {agendas.length > 1 && (
              <div className="flex items-center gap-1.5 overflow-x-auto max-w-full mb-3" data-testid="gestion-agendas-bloqueos-pills">
                {agendas.map((a) => (
                  <AgendaPill key={a.id} agenda={a} selected={a.id === selAgendaId} onClick={() => setSelAgendaId(a.id)} />
                ))}
              </div>
            )}

            {loadingBloqueos ? (
              <div className="cjo-card p-8 text-center text-sm text-muted-foreground">Cargando…</div>
            ) : bloqueos.length === 0 ? (
              <div className="cjo-card p-8 text-center" data-testid="gestion-agendas-bloqueos-empty">
                <CalendarOff className="w-8 h-8 text-[#2E6E5E] mx-auto mb-2" />
                <div className="text-sm font-medium text-[#13231E]">Sin cierres ni aperturas programados</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {selAgenda ? `La agenda ${selAgenda.name} no tiene cierres ni aperturas en los próximos 12 meses.` : ''}
                </p>
              </div>
            ) : (
              <div className="cjo-card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#F0F6F2] border-b border-[#D9E3DD]">
                      <tr className="text-left text-xs uppercase tracking-wider text-[#55677a]">
                        <th className="py-3 px-4">Tipo</th>
                        <th className="py-3 px-4">Fechas</th>
                        <th className="py-3 px-4">Horario</th>
                        <th className="py-3 px-4">Motivo</th>
                        <th className="py-3 px-4 text-right">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#D9E3DD]">
                      {bloqueos.map((b) => (
                        <tr key={b.id} className="hover:bg-[rgba(46,110,94,0.05)]" data-testid={`gestion-agendas-bloqueo-row-${b.id}`}>
                          <td className="py-3 px-4">
                            {b.tipo === 'apertura' ? (
                              <Badge variant="outline" className="bg-[rgba(22,163,74,0.08)] text-green-700 border-green-200">
                                Apertura
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-[rgba(220,38,38,0.08)] text-red-700 border-red-200">
                                Cierre
                              </Badge>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm tabular-nums">
                            {b.date_from === b.date_to ? fmtDate(b.date_from) : `${fmtDate(b.date_from)} — ${fmtDate(b.date_to)}`}
                          </td>
                          <td className="py-3 px-4 text-sm tabular-nums">
                            {b.all_day ? 'Día completo' : `${b.start || ''}–${b.end || ''}`}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {b.reason || <span className="text-muted-foreground">—</span>}
                            {b.created_by_name && (
                              <div className="text-[11px] text-muted-foreground mt-0.5">{b.created_by_name}</div>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            {/* Vacaciones, días libres y bloqueos de un patrón
                                (14/10/2026): solo se miran aquí. Se cambian en
                                Agendas disponibles (borrar aquí el de un patrón
                                lo quitaba de todo el patrón; el servidor lo
                                rechaza). */}
                            {esDeAgendasDisponibles(b) ? (
                              <Link
                                to="/agendas-disponibles"
                                className="text-xs text-muted-foreground hover:underline"
                                data-testid={`gestion-agendas-bloqueo-solo-lectura-${b.id}`}
                              >
                                Se gestiona en Agendas disponibles
                              </Link>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive"
                                onClick={() => setDeletingBloqueo(b)}
                                aria-label="Eliminar"
                                data-testid={`gestion-agendas-bloqueo-delete-${b.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>

          {/* ------------------------------------------------------------ */}
          {/* Conceptos de cita                                             */}
          {/* ------------------------------------------------------------ */}
          <section data-testid="gestion-agendas-section-conceptos">
            <SectionTitle
              title="Conceptos de cita"
              note="Las cirugías también se dan como citas: crea aquí sus conceptos y duración."
              action={
                <Button onClick={openNewConcepto} className="h-9 bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="concepto-nuevo">
                  <Plus className="w-4 h-4 mr-1.5" /> Nuevo concepto
                </Button>
              }
            />

            {conceptosListado.length === 0 ? (
              <div className="cjo-card p-8 text-center text-sm text-muted-foreground">
                No hay conceptos de cita. Crea el primero con “Nuevo concepto”.
              </div>
            ) : (
              <div className="cjo-card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#F0F6F2] border-b border-[#D9E3DD]">
                      <tr className="text-left text-xs uppercase tracking-wider text-[#55677a]">
                        <th className="py-3 px-4">Nombre</th>
                        <th className="py-3 px-4">Categoría</th>
                        <th className="py-3 px-4">Duración por defecto</th>
                        <th className="py-3 px-4">Orden</th>
                        <th className="py-3 px-4 text-right">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#D9E3DD]">
                      {conceptosListado.map((c) => (
                        <tr
                          key={c.id}
                          className={`hover:bg-[rgba(46,110,94,0.05)] ${c.active === false ? 'opacity-60' : ''}`}
                          data-testid={`gestion-agendas-concepto-row-${c.code || c.id}`}
                          data-retirado={c.active === false ? '1' : undefined}
                        >
                          <td className="py-3 px-4 text-sm">
                            <div className="flex items-center gap-2.5">
                              <span
                                className="w-2.5 h-2.5 rounded-full shrink-0"
                                style={{ backgroundColor: c.color || '#2E6E5E' }}
                                aria-hidden="true"
                              />
                              <span className="font-medium text-[#13231E]">{c.name}</span>
                              {c.active === false && (
                                <Badge variant="outline" className="text-[10px] uppercase tracking-wide text-muted-foreground" title="Ya no se ofrece al dar citas; las citas que lo llevan siguen igual">
                                  Retirado
                                </Badge>
                              )}
                              {MARCAS_CONCEPTO.filter((m) => c[m.clave]).map((m) => (
                                <Badge
                                  key={m.clave}
                                  variant="outline"
                                  className="text-[10px] text-[#2E6E5E] border-[rgba(46,110,94,0.35)]"
                                  title={m.clave === 'admite_reserva' && (c.reserva_cobros || []).length
                                    ? `${m.ayuda} Cobros que la pagan: ${c.reserva_cobros.map((t) => `«${t}»`).join(', ')}.`
                                    : m.ayuda}
                                  data-testid={`gestion-agendas-concepto-marca-${c.code || c.id}-${m.clave}`}
                                >
                                  {m.clave === 'admite_reserva'
                                    ? `${m.corto} · ${(MODOS_RESERVA.find((x) => x.valor === (c.reserva_modo || MODO_RESERVA_POR_DEFECTO)) || {}).corto || 'a mano'}`
                                    : m.corto}
                                </Badge>
                              ))}
                              {reservaSinCobros(c) && (
                                <Badge
                                  variant="outline"
                                  className="text-[10px] text-amber-800 border-amber-400 bg-amber-50"
                                  title="Sin ningún cobro que la pague, la reserva no se paga sola: hay que quitarla a mano. Edita el tipo y escribe los cobros que la pagan."
                                  data-testid={`gestion-agendas-concepto-reserva-aviso-${c.code || c.id}`}
                                >
                                  ▸ Reserva sin cobros
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            {c.category === 'cirugia' ? (
                              <Badge className="bg-[#2E6E5E] text-white border-[#2E6E5E] hover:bg-[#2E6E5E]">Cirugía</Badge>
                            ) : (
                              <Badge variant="outline" className="bg-[rgba(46,110,94,0.12)] text-[#13231E] border-[rgba(46,110,94,0.28)]">
                                Consulta
                              </Badge>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm tabular-nums">{c.default_minutes} min</td>
                          <td className="py-3 px-4 text-sm tabular-nums text-muted-foreground">{c.sort_order ?? 0}</td>
                          <td className="py-3 px-4 text-right whitespace-nowrap">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditConcepto(c)}
                              aria-label={`Editar ${c.name}`}
                              data-testid={`gestion-agendas-concepto-edit-${c.code || c.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            {c.active === false ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 text-[#2E6E5E] border-[#2E6E5E]/40 hover:bg-[#2E6E5E]/10"
                                onClick={() => reactivateConcepto(c)}
                                disabled={reactivatingId === c.id}
                                title="Volver a ofrecerlo al dar citas"
                                data-testid={`gestion-agendas-concepto-reactivar-${c.code || c.id}`}
                              >
                                {reactivatingId === c.id
                                  ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                  : <RotateCcw className="w-4 h-4 mr-1" />}
                                Reactivar
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive"
                                onClick={() => setDeactivatingConcepto(c)}
                                aria-label={`Desactivar ${c.name}`}
                                data-testid={`gestion-agendas-concepto-delete-${c.code || c.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>
        </div>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* Diálogos                                                          */}
      {/* ---------------------------------------------------------------- */}

      <AgendaConfigDialog
        open={!!configAgenda}
        onOpenChange={(v) => {
          if (!v) setConfigAgenda(null);
        }}
        agenda={configAgenda}
        conceptos={activeConceptos}
        onSaved={() => {
          reloadAgendas().catch(() => {});
        }}
      />

      {admin && (
        <AgendaNuevaDialog
          open={nuevaOpen}
          onOpenChange={setNuevaOpen}
          agendas={agendas}
          onCreated={() => {
            reloadAgendas().catch(() => {});
          }}
        />
      )}

      <BloqueoDialog
        open={!!bloqueoTipo}
        onOpenChange={(v) => {
          if (!v) setBloqueoTipo(null);
        }}
        agenda={selAgenda}
        defaultDate={todayStr()}
        defaultTipo={bloqueoTipo || 'cierre'}
        onSaved={reloadBloqueos}
      />

      <AlertDialog
        open={!!deletingBloqueo}
        onOpenChange={(v) => {
          if (!v) setDeletingBloqueo(null);
        }}
      >
        <AlertDialogContent data-testid="gestion-agendas-bloqueo-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deletingBloqueo?.tipo === 'apertura' ? '¿Eliminar esta apertura?' : '¿Eliminar este cierre?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deletingBloqueo
                ? `${deletingBloqueo.tipo === 'apertura' ? 'Apertura' : 'Cierre'} del ${
                    deletingBloqueo.date_from === deletingBloqueo.date_to
                      ? fmtDate(deletingBloqueo.date_from)
                      : `${fmtDate(deletingBloqueo.date_from)} al ${fmtDate(deletingBloqueo.date_to)}`
                  }${deletingBloqueo.reason ? ` · ${deletingBloqueo.reason}` : ''}. Esta acción no se puede deshacer.`
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={deletingBloqueoBusy}
              onClick={(e) => {
                e.preventDefault();
                confirmDeleteBloqueo();
              }}
              data-testid="gestion-agendas-bloqueo-delete-confirm-btn"
            >
              {deletingBloqueoBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ConceptoDialog
        open={conceptoDialogOpen}
        onOpenChange={setConceptoDialogOpen}
        initial={editingConcepto}
        onSaved={reloadConceptos}
      />

      <AlertDialog
        open={!!deactivatingConcepto}
        onOpenChange={(v) => {
          if (!v) setDeactivatingConcepto(null);
        }}
      >
        <AlertDialogContent data-testid="gestion-agendas-concepto-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Desactivar “{deactivatingConcepto?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              El concepto dejará de estar disponible al crear nuevas citas. Las citas existentes no se ven afectadas y podrás reactivarlo desde esta misma lista.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={deactivatingBusy}
              onClick={(e) => {
                e.preventDefault();
                confirmDeactivateConcepto();
              }}
              data-testid="gestion-agendas-concepto-delete-confirm-btn"
            >
              {deactivatingBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Desactivar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
